<script setup>
import { computed } from "vue";

/* Reputation hero gauge — dark spotlight ring with gradient + glow.
   Ported from the design HeroGauge. */
const props = defineProps({
  value: { type: Number, default: 0 },
  max: { type: Number, default: 100 },
  size: { type: Number, default: 124 },
  stroke: { type: Number, default: 11 },
  grade: { type: String, default: "-" },
});

const r = computed(() => (props.size - props.stroke) / 2);
const c = computed(() => 2 * Math.PI * r.value);
const cx = computed(() => props.size / 2);
const frac = computed(() =>
  Math.max(0, Math.min(1, props.value / (props.max || 1))),
);
const off = computed(() => c.value * (1 - frac.value));
const tip = computed(() => {
  const ang = ((-90 + frac.value * 360) * Math.PI) / 180;
  return {
    x: cx.value + Math.cos(ang) * r.value,
    y: cx.value + Math.sin(ang) * r.value,
  };
});
</script>

<template>
  <div
    :style="{
      position: 'relative',
      width: size + 'px',
      height: size + 'px',
      flex: '0 0 auto',
    }"
  >
    <svg
      :width="size"
      :height="size"
      style="transform: rotate(-90deg); overflow: visible"
    >
      <defs>
        <linearGradient id="mHeroG" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#F7C669" />
          <stop offset="1" stop-color="#EE9F12" />
        </linearGradient>
        <filter id="mHeroGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <circle
        :cx="cx"
        :cy="cx"
        :r="r"
        fill="none"
        stroke="rgba(255,255,255,.12)"
        :stroke-width="stroke"
      />
      <circle
        :cx="cx"
        :cy="cx"
        :r="r"
        fill="none"
        stroke="url(#mHeroG)"
        :stroke-width="stroke"
        stroke-linecap="round"
        :stroke-dasharray="c"
        :stroke-dashoffset="off"
        filter="url(#mHeroGlow)"
      />
    </svg>
    <span
      :style="{
        position: 'absolute',
        left: tip.x - 3.5 + 'px',
        top: tip.y - 3.5 + 'px',
        width: '7px',
        height: '7px',
        borderRadius: '50%',
        background: '#FFE6B0',
        boxShadow: '0 0 8px 2px rgba(247,198,105,.8)',
      }"
    />
    <div
      style="
        position: absolute;
        inset: 0;
        display: grid;
        place-items: center;
        text-align: center;
      "
    >
      <div>
        <div
          class="btm-num"
          style="
            font-size: 40px;
            font-weight: 800;
            line-height: 1;
            letter-spacing: -0.04em;
          "
        >
          {{ value
          }}<span style="font-size: 15px; color: rgba(246, 238, 223, 0.5)"
            >/{{ max }}</span
          >
        </div>
        <div
          style="
            margin-top: 3px;
            font-family: var(--mono);
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.1em;
            color: rgba(246, 238, 223, 0.6);
          "
        >
          GRADE {{ grade }}
        </div>
      </div>
    </div>
  </div>
</template>
